import { ClubService, ImageConversionService, TagsService, TaskService } from '@core/services';
import { inject } from '@angular/core';
import { GroupEntity, SubTaskAssignedTo, SubTaskEntity, TagEntity, TaskCollaborator, TaskEntity, TaskTagEntity, UserEntity } from '@core/entities';
import { plainToClass, plainToInstance } from 'class-transformer';
import { map } from 'rxjs/operators';
import { UseUser } from '@core/usecases/useUser';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { GenerateImageUrlWithToken, GetUserId } from '@core/utils';
import { SectionType, TagType } from '@core/enums';
import { UseClub } from '@core/usecases/useClub';
import { PermissionService } from '@auth';
import { UseNotification } from '@core/usecases/useNotification';

@UntilDestroy()
export class UseTask {
  private readonly _taskService = inject(TaskService);
  private readonly _tagsService = inject(TagsService);
  private readonly _clubService = inject(ClubService);
  private readonly _imageConversionService = inject(ImageConversionService);
  private readonly _useUser = new UseUser();
  private readonly _useClub = new UseClub();
  private readonly _userPermissions = inject(PermissionService);
  private readonly _useNotification = new UseNotification();

  getAllApprovedTasks() {
    if (this._userPermissions.isAdmin()) {
      return this._taskService.find().pipe(
        map((response: TaskEntity[]) => {
          return response.map((task) => {
            const transformedItem = plainToClass(TaskEntity, task);
            if (transformedItem && transformedItem.id && transformedItem.image) {
              transformedItem.imageUrl = GenerateImageUrlWithToken(transformedItem.id, SectionType.TASK);
            }
            return transformedItem as TaskEntity;
          });
        }),
      );
    } else {
      return this._taskService.getTasksByUserId(GetUserId()).pipe(
        map((response: TaskEntity[]) => {
          return response.map((task) => {
            const transformedItem = plainToClass(TaskEntity, task);
            if (transformedItem.subtasks && transformedItem.subtasks.length) {
              transformedItem.subtasks = transformedItem.subtasks.map((subtask) => {
                if (typeof subtask.assignedTo === 'string') {
                  subtask.assignedTo = JSON.parse(subtask.assignedTo);
                }
                if (subtask.assignedTo && subtask.assignedTo.length) {
                  subtask.assignedTo = subtask.assignedTo.map((user) => plainToClass(SubTaskAssignedTo, user));
                }
                return subtask;
              });
            }
            if (transformedItem && transformedItem.id && transformedItem.image) {
              transformedItem.imageUrl = GenerateImageUrlWithToken(transformedItem.id, SectionType.TASK);
            }

            return transformedItem as TaskEntity;
          });
        }),
      );
    }
  }

  deleteTask(id: number): Observable<boolean> {
    return this._taskService.delete(id).pipe(
      map((response) => {
        if (response) {
          return true;
        }
        return false;
      }),
    );
  }

  markTasksAsRead(): Observable<any> {
    return this._taskService.markAsRead();
  }

  createNewTask(formData: FormData): Observable<TaskEntity> {
    return this._taskService.create(formData).pipe(
      map((response: TaskEntity) => {
        return plainToClass(TaskEntity, response);
      }),
    );
  }

  updateTask(formData: FormData, taskId: number): Observable<TaskEntity> {
    return this._taskService.update(formData, taskId).pipe(
      map((response: TaskEntity) => {
        return plainToClass(TaskEntity, response);
      }),
    );
  }

  getTaskTags(): Observable<TagEntity[]> {
    return this._tagsService.find(null, { type: TagType.TASK }).pipe(
      map((response) => {
        return response.map((item: TagEntity) => {
          return plainToClass(TagEntity, item);
        }) as TagEntity[];
      }),
    );
  }

  getTaskById(taskId: number): Observable<TaskEntity> {
    return this._taskService.get(taskId).pipe(
      map((response: TaskEntity) => {
        if (response) {
          const taskEntity = plainToClass(TaskEntity, response);

          if (taskEntity && taskEntity.id && taskEntity.image) {
            taskEntity.imageUrl = GenerateImageUrlWithToken(taskEntity.id, SectionType.TASK);
          }

          taskEntity.tags = taskEntity.tags.map((item: any) => {
            item = plainToClass(TaskTagEntity, item as TaskTagEntity);
            return item.tagName;
          });

          // Below transformation is for Updated Record

          if (taskEntity.updatedRecord) {
            taskEntity.updatedRecord = plainToInstance(TaskEntity, JSON.parse(taskEntity.updatedRecord as any));

            taskEntity.updatedRecord.subtasks = JSON.parse(taskEntity.updatedRecord.subtasks as any);

            if (taskEntity.updatedRecord.subtasks && taskEntity.updatedRecord.subtasks.length) {
              taskEntity.updatedRecord.subtasks = taskEntity.updatedRecord.subtasks.map((subtask) => {
                subtask = plainToInstance(SubTaskEntity, subtask);

                if (typeof subtask.assignedTo === 'string') {
                  subtask.assignedTo = JSON.parse(subtask.assignedTo);
                }
                if (subtask.assignedTo && subtask.assignedTo.length) {
                  subtask.assignedTo = subtask.assignedTo.map((user) => plainToClass(SubTaskAssignedTo, user));
                }
                return subtask;
              });
            }

            if (taskEntity?.updatedRecord?.tags) {
              taskEntity.updatedRecord.tags = JSON.parse(taskEntity?.updatedRecord.tags as any);
            }

            if (taskEntity?.updatedRecord?.collaborators) {
              taskEntity.updatedRecord.collaborators = JSON.parse(taskEntity.updatedRecord.collaborators as any);
              taskEntity.updatedRecord.collaborators = taskEntity.updatedRecord.collaborators.map((collaborator: any) => plainToInstance(TaskCollaborator, collaborator));
            }

            if (taskEntity?.updatedRecord.file) {
              taskEntity.updatedRecord.imageUrl = GenerateImageUrlWithToken(taskEntity.id, SectionType.UPDATE_TASK);
            }
          }

          return taskEntity;
        }
        return null;
      }),
    );
  }

  getTaskCollaboratorsWithIdsOnly(taskId: number): Observable<number[]> {
    return this._taskService.getCollaborators(taskId).pipe(
      map((response) => {
        return response.map((item: any) => item.user[0].id);
      }),
    );
  }

  getTaskCollaborators(taskId: number): Observable<UserEntity[]> {
    return this._taskService.getCollaborators(taskId).pipe(
      map((response) => {
        return response.map((item: any) => {
          const transformedEntity = plainToInstance(UserEntity, item.user[0]);
          if (transformedEntity?.deleted) {
            transformedEntity.firstName = 'Deleted';
            transformedEntity.lastName = 'User';
            transformedEntity.memberId = '0';
          }
          return transformedEntity;
        });
      }),
    );
  }

  getTeamUsersAndGroups(): Observable<{ users: UserEntity[]; groups: GroupEntity[]; rooms: any }> {
    return this._useClub.getClubGroupsRoomsAndUsers().pipe(
      map((response) => {
        return {
          users: response.users,
          groups: this._userPermissions?.isAdmin()
            ? response.groups
            : (response.groups
                .filter((group: GroupEntity) => {
                  const isParticipant = group.isApprovedMember;
                  const isGroupCreator = group.createdBy === group.currentUserId;
                  return isParticipant || isGroupCreator;
                })
                .map((group: GroupEntity) => plainToClass(GroupEntity, group)) as GroupEntity[]),
          rooms: response.rooms,
        };
      }),
    );
  }

  getTasksUserImage(tasks: TaskEntity[]) {
    tasks.map((task) => {
      this._useUser
        .getUserPhoto(+task.user.memberId)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: (image) => {
            task.user.imageUrl = image;
          },
        });
    });
  }

  markTaskAsComplete(taskId: number): Observable<any> {
    return this._taskService.approveTaskById(taskId);
  }

  markSubtaskAsComplete(subtaskId: number): Observable<any> {
    return this._taskService.approveSubtaskById(subtaskId);
  }

  approveTaskByAdmin(taskId: number): Observable<any> {
    return this._useNotification.adminApprovedTasks(taskId);
  }

  approveTaskUpdateByAdmin(taskId: number): Observable<any> {
    return this._useNotification.adminApprovedUpdateTasks(taskId);
  }

  denyTaskByAdmin(taskId: number, reason: string): Observable<any> {
    return this._useNotification.adminUnapprovedTasks(taskId, reason);
  }
}
